import React, {
  useEffect,
  useState
} from "react";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
// P4-2: create / edit / disable only through itemService (code = prefix + item_id
// number, items + stock doc in ONE batch, name-key duplicate check).
import { createItem, updateItem, disableItem, canDisableItem } from "../../services/itemService";
// D-291 (P3b): HSN code + GST % on the item, used later to pre-fill PO and
// vendor-bill lines (and HSN already flows to client invoices - CreateInvoice
// reads items.hsn_code). Both optional: blank GST % = "not set", the PO line
// must then have GST typed - never a silent 18.
import { checkHsn, ITEM_GST_OPTIONS } from "../../utils/gstBill";

// "" -> null (not set); otherwise the number. Item GST % is optional.
const gstPercentOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const gstLabel = (v) => (v === null || v === undefined || v === "" ? "" : `${v}%`);

export default function ItemMaster() {

  const [items, setItems] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  // ✅ Vendor state
  const [vendors, setVendors] = useState([]);

  // ✅ Edit state
  const [editingId,   setEditingId]   = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editSaving,  setEditSaving]  = useState(false);

  const [form, setForm] =
    useState({
      item_name:              "",
      specification:          "",
      item_code:              "",
      unit:                   "nos",
      category:               "MATERIAL",
      minimum_stock:          0,
      preferred_vendor_id:    "",
      preferred_vendor_name:  "",
      hsn_code:               "",
      gst_percent:            "",
    });

  // ── SEARCH (Item Master list) ──────────────────────────────────────────
  // Client-side filter — Item Master already loads all active items in one
  // query (fetchItems() above), so a second query per keystroke would be
  // an N+1 anti-pattern. Filters across item_name + specification +
  // item_code so a technician can find an item whether they remember the
  // short name ("copper pipe") or a spec detail ("22.2mm").
  const [searchTerm, setSearchTerm] = useState("");

  const user      = JSON.parse(localStorage.getItem("user") || "{}");
const companyId = user?.company_id || "";

  // =================================================
  // FETCH ITEMS
  // =================================================

  const fetchItems =
    async () => {

      setLoading(true);

      try {

        const q = query(
  collection(db, "items"),
  where("company_id", "==", companyId),
  where("is_active",  "==", true)
);

        const snap =
          await getDocs(q);

        const list =
          snap.docs.map(
            (docSnap) => ({

              id:
                docSnap.id,

              ...docSnap.data(),
            })
          );

        list.sort(
          (a, b) =>

            (
              a.item_name || ""
            ).localeCompare(

              b.item_name || ""
            )
        );

        setItems(list);

      } catch (err) {

        console.error(
          "Fetch Items Error:",
          err
        );
      }

      setLoading(false);
    };

  // =================================================
  // FETCH VENDORS
  // =================================================
  const fetchVendors = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "vendors"),
        where("company_id", "==", companyId)
      ));
      setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Fetch Vendors Error:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchItems(); fetchVendors(); }, []);

  // =================================================
  // HANDLE CHANGE
  // =================================================

  const handleChange =
    (e) => {

      const {
        name,
        value
      } = e.target;

      // P4-2: the item code is no longer built from the name — itemService
      // sets it from the item id when the item is saved.
      setForm({ ...form, [name]: value });
    };

  // =================================================
  // RESET FORM
  // =================================================

  const resetForm =
    () => {
      setForm({
        item_name:             "",
        specification:         "",
        item_code:             "",
        unit:                  "",
        category:              "MATERIAL",
        minimum_stock:         0,
        preferred_vendor_id:   "",
        preferred_vendor_name: "",
        hsn_code:              "",
        gst_percent:           "",
      });
    };

// =================================================
// ADD ITEM
// =================================================

const handleSubmit =
  async () => {

    if (!form.item_name?.trim()) {
      alert("Item Name is required."); return;
    }
    if (!form.unit) {
      alert("Please select a Unit (meter / nos / kg etc)."); return;
    }
    if (!form.category) {
      alert("Please select a Category (Material or Consumable)."); return;
    }
    const hsnCheck = checkHsn(form.hsn_code);
    if (!hsnCheck.ok) {
      alert(hsnCheck.reason); return;
    }

    setLoading(true);

    try {
      // P4-2: one batch (items + inventory_summary/{item_id}); the service
      // re-reads Item Master and refuses a name that already exists
      // (own company + shared items, "250 mm" = "250mm").
      const created = await createItem({
        company_id:            companyId,
        item_name:             form.item_name,
        specification:         form.specification,
        unit:                  form.unit,
        category:              form.category,
        minimum_stock:         form.minimum_stock,
        preferred_vendor_id:   form.preferred_vendor_id,
        preferred_vendor_name: form.preferred_vendor_name,
        hsn_code:              hsnCheck.hsn,
        gst_percent:           gstPercentOrNull(form.gst_percent),
      });

      alert(`Item Added — ${created.item_code}`);

      resetForm();

      fetchItems();

    } catch (err) {

      console.error(
        "Save Item Error:",
        err
      );

      alert(err?.message || "Failed to save item");
    }

    setLoading(false);
  };

  // =================================================
  // EDIT ITEM
  // =================================================
  const handleEditSave = async () => {
    if (!editForm.item_name?.trim()) { alert("Item name required"); return; }
    const editHsn = checkHsn(editForm.hsn_code);
    if (!editHsn.ok) { alert(editHsn.reason); return; }
    setEditSaving(true);
    try {
      // P4-2: item + its own stock doc in ONE batch; the item code never changes.
      await updateItem({
        company_id: companyId,
        docId:      editingId,
        changes: {
          item_name:             editForm.item_name,
          specification:         editForm.specification || "",
          unit:                  editForm.unit,
          category:              editForm.category,
          minimum_stock:         editForm.minimum_stock,
          preferred_vendor_id:   editForm.preferred_vendor_id   || "",
          preferred_vendor_name: editForm.preferred_vendor_name || "",
          hsn_code:              editHsn.hsn,
          gst_percent:           gstPercentOrNull(editForm.gst_percent),
        },
      });
      setEditingId(null);
      setEditForm({});
      fetchItems();
      alert("✅ Item updated");
    } catch (err) {
      console.error("Edit Error:", err);
      alert("Failed to update: " + err.message);
    } finally {
      setEditSaving(false);
    }
  };

  // =================================================
  // DISABLE ITEM
  // =================================================

  const handleDisable =
    async (id) => {

      if (

        !window.confirm(
          "Disable this item?"
        )
      ) {

        return;
      }

      try {

        await disableItem({ company_id: companyId, docId: id });

        fetchItems();

      } catch (err) {

        console.error(
          "Disable Error:",
          err
        );
        alert(err?.message || "Failed to disable item");
      }
    };

  return (

    <div className="min-h-screen bg-gray-100 p-6">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">

          <div className="flex items-center justify-between mb-6">

            <h2 className="text-2xl font-bold">
              Add New Item
            </h2>

            
          </div>

          {/* FORM */}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <input
              type="text"
              name="item_name"
              placeholder="Item Name (short — e.g. 'Copper Pipe 22.2mm')"
              value={
                form.item_name
              }
              onChange={
                handleChange
              }
              className="border rounded-lg px-4 py-2"
            />

            <input
              type="text"
              name="specification"
              placeholder="Specification (optional — e.g. 'Softdrawn, 1.2mm thk, 19mm insulation')"
              value={form.specification}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2 md:col-span-2"
            />

            <input
              type="text"
              name="item_code"
              placeholder="Item code — set from the item id when saved"
              value=""
              readOnly
              className="border rounded-lg px-4 py-2 bg-gray-100 text-gray-500"
            />

            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            >
              <option value="">— Select Unit *</option>
              <option value="nos">Nos</option>
              <option value="meter">Meter</option>
              <option value="kg">Kg</option>
              <option value="ltr">Ltr</option>
              <option value="box">Box</option>
              <option value="set">Set</option>
              <option value="rolls">Rolls</option>
              <option value="pcs">Pcs</option>
              <option value="sqft">Sqft</option>
              <option value="sqm">Sqm</option>
              <option value="rmt">Rmt</option>
              <option value="rft">Rft</option>
              <option value="job">Job</option>
              <option value="lot">Lot</option>
            </select>

            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            >
              <option value="MATERIAL">Material</option>
          
              <option value="CONSUMABLE">Consumable</option>
              
            </select>
 
            <input
              type="number"
              name="minimum_stock"
              placeholder="Min Stock (reorder level)"
              min="0"
              value={form.minimum_stock}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            />

            <select
              value={form.preferred_vendor_id}
              onChange={e => {
                const v = vendors.find(v => v.id === e.target.value);
                setForm(f => ({
                  ...f,
                  preferred_vendor_id:   v ? v.id : "",
                  preferred_vendor_name: v ? (v.vendor_name || v.name || "") : "",
                }));
              }}
              className="border rounded-lg px-4 py-2"
            >
              <option value="">— Preferred Vendor (Optional) —</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.vendor_name || v.name || v.id}
                </option>
              ))}
            </select>

            <input
              type="text"
              inputMode="numeric"
              name="hsn_code"
              placeholder="HSN / SAC code (optional — 4, 6 or 8 digits)"
              value={form.hsn_code}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            />

            <select
              name="gst_percent"
              value={form.gst_percent}
              onChange={handleChange}
              className="border rounded-lg px-4 py-2"
            >
              <option value="">— GST % (optional) —</option>
              {ITEM_GST_OPTIONS.map(r => (
                <option key={r} value={String(r)}>{r}%</option>
              ))}
            </select>
          </div>

          {/* BUTTONS */}

          <div className="flex gap-4 mt-6">

            <button
              onClick={
                handleSubmit
              }
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
            >

              {

                loading

                  ? "Processing..."

                  : "Add Item"
              }
            </button>

            <button
              onClick={
                resetForm
              }
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded-lg"
            >
              Reset
            </button>
          </div>
        </div>

        {/* ITEMS */}

        <div className="bg-white rounded-xl shadow-md p-6">

          <h2 className="text-2xl font-bold mb-4">
            Items Master List
          </h2>

          <input
            type="text"
            placeholder="🔍 Search by name, specification, code or item id..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full mb-4"
          />

          {loading ? (

            <div>
              Loading...
            </div>

          ) : (

            <div className="overflow-x-auto">

              <table className="w-full border-collapse">

                <thead>

                  <tr className="bg-gray-800 text-white">

                    <th className="p-3 text-left">
                      Item ID
                    </th>

                    <th className="p-3 text-left">
                      Item Name
                    </th>

                    <th className="p-3 text-left">
                      Specification
                    </th>

                    <th className="p-3 text-left">
                      Code
                    </th>

                    <th className="p-3 text-left">
                      Unit
                    </th>

                    <th className="p-3 text-left">HSN</th>
                    <th className="p-3 text-left">GST %</th>
                    <th className="p-3 text-left">Category</th>
                    <th className="p-3 text-left">Min Stock</th>
                    <th className="p-3 text-left">Preferred Vendor</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items
                    .filter((item) => {
                      if (!searchTerm.trim()) return true;
                      const q = searchTerm.trim().toLowerCase();
                      return (
                        (item.item_name     || "").toLowerCase().includes(q) ||
                        (item.specification || "").toLowerCase().includes(q) ||
                        (item.item_code     || "").toLowerCase().includes(q) ||
                        (item.item_id       || "").toLowerCase().includes(q)
                      );
                    })
                    .map((item) =>
                    editingId === item.id ? (
                      <tr key={item.id} className="border-b bg-blue-50">
                        <td className="p-3 font-semibold text-blue-600">
                          {item.item_id || "MISSING"}
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.item_name || ""}
                            onChange={e => setEditForm(f => ({ ...f, item_name: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.specification || ""}
                            onChange={e => setEditForm(f => ({ ...f, specification: e.target.value }))}
                            placeholder="Specification"
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            value={editForm.item_code || ""}
                            readOnly
                            title="Set from the item id — cannot be edited"
                            className="border rounded px-2 py-1 text-sm w-full bg-gray-100 text-gray-500"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.unit || "nos"}
                            onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="nos">Nos</option>
                            <option value="meter">Meter</option>
                            <option value="kg">Kg</option>
                            <option value="box">Box</option>
                            <option value="set">Set</option>
                            <option value="ltr">Ltr</option>
                            <option value="rolls">Rolls</option>
                            <option value="pcs">Pcs</option>
                            <option value="sqft">Sqft</option>
                            <option value="sqm">Sqm</option>
                            <option value="rmt">Rmt</option>
                            <option value="rft">Rft</option>
                            <option value="job">Job</option>
                            <option value="lot">Lot</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <input
                            inputMode="numeric"
                            value={editForm.hsn_code || ""}
                            onChange={e => setEditForm(f => ({ ...f, hsn_code: e.target.value }))}
                            placeholder="HSN"
                            className="border rounded px-2 py-1 text-sm w-24"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.gst_percent ?? ""}
                            onChange={e => setEditForm(f => ({ ...f, gst_percent: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="">— not set —</option>
                            {ITEM_GST_OPTIONS.map(r => (
                              <option key={r} value={String(r)}>{r}%</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.category || "MATERIAL"}
                            onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="MATERIAL">Material</option>
                            <option value="CONSUMABLE">Consumable</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <input
                            type="number" min="0"
                            value={editForm.minimum_stock || 0}
                            onChange={e => setEditForm(f => ({ ...f, minimum_stock: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-20"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={editForm.preferred_vendor_id || ""}
                            onChange={e => {
                              const v = vendors.find(v => v.id === e.target.value);
                              setEditForm(f => ({
                                ...f,
                                preferred_vendor_id:   v ? v.id : "",
                                preferred_vendor_name: v ? (v.vendor_name || v.name || "") : "",
                              }));
                            }}
                            className="border rounded px-2 py-1 text-sm w-40"
                          >
                            <option value="">— None —</option>
                            {vendors.map(v => (
                              <option key={v.id} value={v.id}>
                                {v.vendor_name || v.name || v.id}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">Active</td>
                        <td className="p-3 flex gap-2">
                          <button
                            onClick={handleEditSave}
                            disabled={editSaving}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                          >
                            {editSaving ? "..." : "Save"}
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditForm({}); }}
                            className="bg-gray-400 text-white px-3 py-1 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id} className="border-b">
                        <td className="p-3 font-semibold text-blue-600">
                          {item.item_id || "MISSING"}
                        </td>
                        <td className="p-3">{item.item_name}</td>
                        <td className="p-3 text-sm text-slate-500">
                          {item.specification || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3">{item.item_code}</td>
                        <td className="p-3">{item.unit}</td>
                        <td className="p-3 text-sm">
                          {item.hsn_code || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3 text-sm">
                          {gstLabel(item.gst_percent) || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3">{item.category}</td>
                        <td className="p-3 text-center">{item.minimum_stock || 0}</td>
                        <td className="p-3 text-sm text-slate-600">
                          {item.preferred_vendor_name || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3">{item.is_active ? "Active" : "Disabled"}</td>
                        <td className="p-3 flex gap-2">
                          <button
                            onClick={() => {
                              setEditingId(item.id);
                              setEditForm({
                                item_name:             item.item_name             || "",
                                specification:         item.specification         || "",
                                item_code:             item.item_code             || "",
                                unit:                  item.unit                  || "nos",
                                category:              item.category              || "MATERIAL",
                                minimum_stock:         item.minimum_stock         || 0,
                                preferred_vendor_id:   item.preferred_vendor_id   || "",
                                preferred_vendor_name: item.preferred_vendor_name || "",
                                hsn_code:              item.hsn_code              || "",
                                gst_percent:           item.gst_percent == null ? "" : String(item.gst_percent),
                              });
                            }}
                            className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                          >
                            Edit
                          </button>
                          {item.is_active && canDisableItem(user.role) && (
                            <button
                              onClick={() => handleDisable(item.id)}
                              className="bg-red-500 text-white px-3 py-1 rounded"
                            >
                              Disable
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}